import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  updateProfile 
} from "firebase/auth";
import { 
  doc, 
  setDoc 
} from "firebase/firestore";
import { auth, db, app } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

// Helper function to generate a unique public ID
const generatePublicId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const length = 16; // Desired length of the ID
  
  // Generate random characters
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
};
// Helper function to generate a unique public ID
const generateCompanyId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const length = 16; // Desired length of the ID
  
  // Generate random characters
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
};


export const signupUser = async (email, password) => {
  try {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address.');
    }

    // Validate password strength
    if (password.length < 6) {
      throw new Error('Password should be at least 6 characters long.');
    }

    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Generate a public ID
    const publicId = generatePublicId();
    const companyId = generateCompanyId();
    
    // Set user's display name as email username
    await updateProfile(user, {
      displayName: email.split('@')[0]
    });

    // Calculate 7 days from now for trial expiry
    const trialEnds = new Date();
    trialEnds.setDate(trialEnds.getDate() + 7);

    // Save user details to Firestore
    const companyData = {
      companyId: companyId,
      email: user.email,
      publicId: publicId,
      createdAt: new Date().toISOString(),
      emailVerified: user.emailVerified,
      subscription: {
        plan: "Free Trial",
        status: "trialing",
        trialEnds: trialEnds.toISOString(),
        billingCycle: null,
        nextBillingDate: null
      }
    };

    const userData = {
      companyId: companyId,
      createdAt: new Date().toISOString(),
      displayName: user.displayName,
      email: user.email,
      emailVerified: user.emailVerified,
      lastLogin: new Date().toISOString(),
      publicId: publicId,
      role: 'admin',
      isActive: true
    };

    await setDoc(doc(db, "users", user.uid), userData);
    await setDoc(doc(db, "companies", companyId), companyData);
    await setDoc(doc(db, "publicUsers", publicId), {
      createdAt: userData.createdAt
    });

    // Create Stripe customer for this company (non-blocking)
    try {
      const functions = getFunctions(app);
      const createStripeCustomer = httpsCallable(functions, "createStripeCustomer");
      await createStripeCustomer({ companyId });
    } catch (err) {
      console.error("Failed to create Stripe customer on signup:", err);
    }

    // Send email verification link
    await sendEmailVerification(user);

    return {
      success: true,
      user,
      message: "Account created successfully! Please check your email to verify your account."
    };
  } catch (error) {
    console.error('Signup error:', error);
    let errorMessage = 'Failed to create account. Please try again.';
    
    // Map Firebase error codes to user-friendly messages
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered. Please use a different email or try logging in.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password accounts are not enabled.';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password is too weak. Please choose a stronger password with at least 6 characters.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many signup attempts. Please try again later.';
        break;
      default:
        // Use the default error message
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};
